import posthog from 'posthog-js';

import { PostHogFeatureFlagMap } from 'src/context/flagsContext';
import { bucket } from 'src/utils/bucket';

// W3C Trace Context header carried on every REST call to the Go backend so the
// backend adopts our trace id as its request id. Version 00; the sampled flag
// is a per-trace-id ratio decision (see bucket()), not always-on.
export const TRACEPARENT_HEADER = 'traceparent';
const TRACEPARENT_VERSION = '00';
const TRACEPARENT_FLAGS_SAMPLED = '01';
const TRACEPARENT_FLAGS_UNSAMPLED = '00';
const TRACE_ID_BYTES = 16;
const PARENT_ID_BYTES = 8;

const randomHex = (bytes: number) => {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return Array.from(buffer, (b) => b.toString(16).padStart(2, '0')).join('');
};

export const generateTraceId = () => randomHex(TRACE_ID_BYTES);
export const generateSpanId = () => randomHex(PARENT_ID_BYTES);

/**
 * Mints a fresh trace id + span id. The sampled flag is decided by
 * `bucket(traceId) < traceSampleRatio` — the
 * same decision the backend makes independently from the same trace id, so
 * ratio is a required input rather than an implicit "always sampled".
 */
export const generateTraceparent = (traceSampleRatio: number) => {
  const traceId = generateTraceId();
  const spanId = generateSpanId();
  const flags = bucket(traceId, traceSampleRatio)
    ? TRACEPARENT_FLAGS_SAMPLED
    : TRACEPARENT_FLAGS_UNSAMPLED;
  return `${TRACEPARENT_VERSION}-${traceId}-${spanId}-${flags}`;
};

export const traceIdFromTraceparent = (traceparent: string) =>
  traceparent.split('-')[1];

// The ratios are read from window.App, sourced server-side from SSM
// (otel_trace_sample_ratio, otel_sentry_sample_ratio — see
// src/utils/TraceSampleRatiosSsm.ts). Both default to 0 (never sampled) when
// missing/unparseable, matching the server-side default so an unconfigured
// environment never samples.
const readWindowSampleRatio = (
  key: 'traceSampleRatio' | 'sentrySampleRatio',
): number => {
  if (typeof window === 'undefined') {
    return 0;
  }
  const value = window.App?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

export const getTraceSampleRatio = () =>
  readWindowSampleRatio('traceSampleRatio');

export const getSentrySampleRatio = () =>
  readWindowSampleRatio('sentrySampleRatio');

// Read from the PostHog singleton rather than FlagsContext because the API
// helper is not a React component. The singleton is authoritative once it
// answers, but returns undefined until posthog-js finishes initializing, so
// requests fired before then fall back to the flags the server bootstrapped
// into window.App at page load.
export const isBrowserTraceIdsEnabled = () => {
  try {
    const flag = PostHogFeatureFlagMap.EnableBrowserTraceIds;
    const singletonValue = posthog.isFeatureEnabled(flag);
    if (singletonValue !== undefined) {
      return singletonValue === true;
    }
    if (typeof window === 'undefined') {
      return false;
    }
    return window.App?.posthogBootstrap?.featureFlags?.[flag] === true;
  } catch {
    return false;
  }
};

/**
 * Returns headers with a fresh traceparent attached when the flag is on.
 * The Sentry subset (a span for the ids selected by sentrySampleRatio) is
 * NOT decided here — it lives in AmplifyApiUtils' API.get/post/put/patch/del,
 * because this helper (and getInitForApi, which calls it) is also used by
 * app-builder streaming code that must never get a Sentry span.
 */
export const withTraceparent = (headers: Record<string, unknown>) => {
  if (!isBrowserTraceIdsEnabled()) {
    return headers;
  }
  const traceparent = generateTraceparent(getTraceSampleRatio());
  return { ...headers, [TRACEPARENT_HEADER]: traceparent };
};
